import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowUp, ArrowDown, Minus, Briefcase, TrendingUp, TrendingDown, Activity, Sparkles, ExternalLink,
  ChevronLeft, ChevronRight, Flame, Snowflake, Scale, Clock, Gauge, History,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { fetchNfpDetail, fetchNfpMonthRead } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";

const fmtVal = (v, unit) => {
  if (v == null) return "—";
  const n = Number(v);
  if (unit === "K") return `${n.toFixed(Math.abs(n) % 1 ? 1 : 0)}K`;
  if (unit === "M") return `${n.toFixed(2)}M`;
  if (unit === "%") return `${Math.round(n * 100) / 100}%`;
  return `${Math.round(n * 100) / 100}`;
};
const fmtJobs = (v) => (v == null ? "—" : Math.abs(v) >= 1000 ? `${v > 0 ? "+" : "−"}${(Math.abs(v) / 1000).toFixed(1)}M` : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}K`);
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

function Surprise({ actual, consensus, unit }) {
  if (actual == null || consensus == null) return <span className="text-zinc-700 text-[11px] tabular-nums">--</span>;
  const diff = actual - consensus;
  // colour reflects the actual vs CONSENSUS surprise (above = green, below = red)
  const cls = diff > 0 ? "text-pos" : diff < 0 ? "text-neg" : "text-zinc-500";
  const Arrow = diff > 0 ? ArrowUp : diff < 0 ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums text-[11px] ${cls}`}>
      <Arrow size={10} strokeWidth={2.5} />{fmtVal(Math.abs(diff), unit)}
    </span>
  );
}

// A single actual/consensus/previous metric block for the NFP print header.
// `invert` flips the hot/cool coloring for metrics where LOWER beats consensus
// (e.g. unemployment: a lower-than-expected rate is the "hot"/USD-positive read).
function PrintMetric({ label, actual, consensus, previous, testid, actualRaw, consensusRaw, invert = false }) {
  let cls = "text-zinc-100";
  if (actualRaw != null && consensusRaw != null && actualRaw !== consensusRaw) {
    const hotter = invert ? actualRaw < consensusRaw : actualRaw > consensusRaw;
    cls = hotter ? "text-pos" : "text-neg";
  }
  return (
    <div className="border border-zinc-800 bg-[#09090B] px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
      <div className={`text-3xl font-semibold tabular-nums leading-none mb-2 ${cls}`}>{actual}</div>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-zinc-600">
        <span>cons <span className="text-zinc-400 tabular-nums normal-case">{consensus}</span></span>
        <span>prev <span className="text-zinc-400 tabular-nums normal-case">{previous}</span></span>
      </div>
    </div>
  );
}

function StatTable({ title, rows, positive }) {
  const Icon = positive ? TrendingUp : TrendingDown;
  const accent = positive ? "text-pos" : "text-neg";
  const border = positive ? "border-pos/40" : "border-neg/40";
  return (
    <div className={`border ${border} bg-[#09090B]`} data-testid={`nfp-table-${positive ? "positive" : "negative"}`}>
      <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
        <span className={`text-[11px] uppercase tracking-widest flex items-center gap-1.5 ${accent}`}>
          <Icon size={13} strokeWidth={1.75} /> {title}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">{rows.length} release{rows.length === 1 ? "" : "s"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-zinc-600 text-[11px] uppercase tracking-widest">None in window</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-widest text-zinc-600 border-b border-zinc-900">
              <th className="text-left font-normal px-3 py-1.5">Indicator</th>
              <th className="text-right font-normal px-2 py-1.5">Actual</th>
              <th className="text-right font-normal px-2 py-1.5">Cons</th>
              <th className="text-right font-normal px-2 py-1.5">Prev</th>
              <th className="text-right font-normal px-3 py-1.5">Surprise</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.key}-${r.date}`} className="border-b border-zinc-900/60 hover:bg-zinc-900/40" data-testid={`nfp-row-${r.key}-${r.date}`}>
                <td className="px-3 py-2">
                  <div className="text-zinc-200">{r.indicator}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-600">{r.date} · {r.source}</div>
                </td>
                <td className="text-right px-2 py-2 tabular-nums text-zinc-100 font-medium">{fmtVal(r.actual, r.unit)}</td>
                <td className="text-right px-2 py-2 tabular-nums text-zinc-500">{fmtVal(r.consensus, r.unit)}</td>
                <td className="text-right px-2 py-2 tabular-nums text-zinc-500">{fmtVal(r.previous, r.unit)}</td>
                <td className="text-right px-3 py-2"><Surprise actual={r.actual} consensus={r.consensus} unit={r.unit} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DXYPanel({ dxy }) {
  if (!dxy) {
    return (
      <div className="border border-zinc-800 bg-[#09090B] px-3 py-6 text-center text-zinc-600 text-[11px] uppercase tracking-widest" data-testid="nfp-dxy-panel">
        DXY intraday unavailable for this release
      </div>
    );
  }
  const up = dxy.points > 0;
  const color = up ? "#22c55e" : "#ef4444";
  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="nfp-dxy-panel">
      <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-1.5">
          <Activity size={13} strokeWidth={1.5} className="text-amber-term" /> DXY reaction · {dxy.date}
        </span>
        <span className="flex items-baseline gap-2.5">
          <span className={`text-2xl font-semibold tabular-nums leading-none ${up ? "text-pos" : "text-neg"}`} data-testid="nfp-dxy-points">
            {up ? "+" : ""}{dxy.points?.toFixed(2)}
          </span>
          <span className={`text-[11px] uppercase tracking-widest ${up ? "text-pos" : "text-neg"}`}>
            {up ? "+" : ""}{dxy.pct?.toFixed(2)}%
          </span>
        </span>
      </div>

      <div className="grid grid-cols-4 gap-px bg-zinc-900 text-center">
        {[
          ["Release", dxy.start_price?.toFixed(3), dxy.start_time],
          ["Close", dxy.end_price?.toFixed(3), dxy.end_time],
          ["Session high", dxy.high?.toFixed(3), ""],
          ["Session low", dxy.low?.toFixed(3), ""],
        ].map(([l, v, t]) => (
          <div key={l} className="bg-[#09090B] px-2 py-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-600">{l}</div>
            <div className="text-[13px] tabular-nums text-zinc-100">{v ?? "—"}</div>
            {t ? <div className="text-[9px] tabular-nums text-zinc-600">{t}</div> : null}
          </div>
        ))}
      </div>

      <div className="h-[220px] w-full px-2 pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={dxy.path} margin={{ top: 6, right: 16, left: -6, bottom: 0 }}>
            <defs>
              <linearGradient id="dxyfill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#18181B" vertical={false} />
            <XAxis dataKey="t" tick={{ fontSize: 9, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} minTickGap={40} />
            <YAxis domain={["auto", "auto"]} tick={{ fontSize: 9, fill: "#71717A", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#27272A" }} width={44} tickFormatter={(v) => v.toFixed(2)} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} formatter={(v) => [Number(v).toFixed(3), "DXY"]} />
            <ReferenceLine x={dxy.release_x} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: "NFP 8:30 ET", position: "insideTopLeft", fill: "#f59e0b", fontSize: 9 }} />
            <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill="url(#dxyfill)" isAnimationActive={false} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="px-3 py-2 text-[10px] text-zinc-600 leading-relaxed">
        Points moved by the US Dollar Index from the 8:30 am ET (NFP release) through the 4:00 pm ET session close. Hourly bars — baseline is the release-hour bar open.
      </p>
    </div>
  );
}

function NfpMonthRead({ index, month, pending }) {
  const q = useQuery({
    queryKey: ["nfp-read", index],
    queryFn: () => fetchNfpMonthRead(index),
    staleTime: pending ? 1000 * 60 * 15 : 1000 * 60 * 60,
    placeholderData: keepPreviousData,
    enabled: index != null,
  });
  const d = q.data;
  const fresh = d && d.month === month;  // guard against keepPreviousData showing a stale month's read
  return (
    <div className="border border-zinc-800 bg-[#09090B] px-4 py-3" data-testid="nfp-month-read">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={13} strokeWidth={1.75} className="text-amber-term" />
        <span className="text-[10px] uppercase tracking-widest text-amber-term">
          {pending ? "AI Read · What to expect" : "AI Read · Why it moved"}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">{month}</span>
      </div>
      {!fresh ? (
        <div className="space-y-1.5" data-testid="nfp-read-loading">
          <div className="h-2.5 w-full bg-zinc-800/60 animate-pulse rounded-sm" />
          <div className="h-2.5 w-11/12 bg-zinc-800/60 animate-pulse rounded-sm" />
          <div className="h-2.5 w-3/4 bg-zinc-800/60 animate-pulse rounded-sm" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 pt-1">Scanning the wires…</div>
        </div>
      ) : d?.read ? (
        <>
          <p className="text-[12.5px] leading-relaxed text-zinc-300" data-testid="nfp-read-text">{d.read}</p>
          {d.web_sources?.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 pt-2 border-t border-zinc-900">
              {d.web_sources.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nfp-read-source-${i}`}
                  className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-amber-term transition-colors max-w-[240px] truncate"
                >
                  <ExternalLink size={10} strokeWidth={1.75} className="shrink-0" />
                  <span className="truncate">{s.title}</span>
                </a>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="text-[11px] text-zinc-600 uppercase tracking-widest" data-testid="nfp-read-empty">
          AI read unavailable for this month
        </p>
      )}
    </div>
  );
}

const shortMonth = (label) => {
  if (!label) return "";
  const [m, y] = label.split(" ");
  return `${(m || "").slice(0, 3)} ${y || ""}`.trim();
};

function MonthBoard({ months, selected, onSelect }) {
  const scrollRef = useRef(null);
  if (!months?.length) return null;
  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.7, 200), behavior: "smooth" });
  };
  return (
    <div data-testid="nfp-month-board">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
        Select a month — browse the last {months.length} NFP prints
      </div>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          data-testid="nfp-board-scroll-left"
          aria-label="Scroll months left"
          className="shrink-0 w-8 flex items-center justify-center border border-zinc-800 bg-[#09090B] text-zinc-500 hover:text-amber-term hover:border-zinc-600 transition-colors"
        >
          <ChevronLeft size={16} strokeWidth={2} />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {months.map((m) => {
            const active = m.index === selected;
            const isPending = m.status === "pending";
            const c = m.consensus;
            // colour the payrolls print by ACTUAL vs CONSENSUS (above = green, below = red)
            const cls =
              c == null ? "text-zinc-300" : m.payrolls > c ? "text-pos" : m.payrolls < c ? "text-neg" : "text-zinc-400";
            return (
              <button
                key={m.mkey}
                onClick={() => onSelect(m.index)}
                data-testid={`nfp-month-btn-${m.index}`}
                aria-pressed={active}
                className={`shrink-0 min-w-[92px] px-3 py-2 border text-left transition-colors ${
                  active
                    ? "border-amber-500 bg-amber-500/10"
                    : isPending
                    ? "border-dashed border-zinc-700 bg-transparent hover:border-zinc-500"
                    : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center gap-1">
                  <span className={`text-[10px] uppercase tracking-widest ${active ? "text-amber-term" : "text-zinc-500"}`}>
                    {shortMonth(m.label)}
                  </span>
                  {isPending && (
                    <span className="text-[8px] uppercase tracking-widest text-amber-term/80 border border-amber-term/40 px-1 leading-tight rounded-sm">
                      Upcoming
                    </span>
                  )}
                </div>
                {isPending ? (
                  <div className="text-sm font-semibold tabular-nums mt-0.5 text-zinc-500">
                    {c == null ? "—" : `exp ${fmtJobs(c)}`}
                  </div>
                ) : (
                  <div className={`text-sm font-semibold tabular-nums mt-0.5 ${cls}`}>
                    {fmtJobs(m.payrolls)}
                  </div>
                )}
                {!isPending && m.revision != null && (
                  <div
                    className={`text-[9px] tabular-nums leading-tight ${m.revision > 0 ? "text-pos" : "text-neg"}`}
                    data-testid={`nfp-month-rev-${m.index}`}
                  >
                    {m.revision > 0 ? "▲" : "▼"} {m.revision > 0 ? "+" : ""}{m.revision}K rev
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          data-testid="nfp-board-scroll-right"
          aria-label="Scroll months right"
          className="shrink-0 w-8 flex items-center justify-center border border-zinc-800 bg-[#09090B] text-zinc-500 hover:text-amber-term hover:border-zinc-600 transition-colors"
        >
          <ChevronRight size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function RevisionBadge({ revision, revised }) {
  if (revision == null) return null;
  const up = revision > 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] uppercase tracking-widest ${
        up ? "text-pos border-pos/40 bg-pos/10" : "text-neg border-neg/40 bg-neg/10"
      }`}
      data-testid="nfp-revision-badge"
      title="A later release revised this month's payrolls"
    >
      <History size={12} strokeWidth={1.75} />
      Revised {up ? "+" : ""}{revision}K
      <span className="normal-case tracking-normal text-zinc-400">→ {fmtJobs(revised)}</span>
    </span>
  );
}

function SurpriseScore({ score, label }) {
  if (score == null) return null;
  const cls =
    score > 0.15
      ? "text-pos border-pos/40 bg-pos/10"
      : score < -0.15
      ? "text-neg border-neg/40 bg-neg/10"
      : "text-zinc-400 border-zinc-700 bg-zinc-800/30";
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] uppercase tracking-widest ${cls}`}
      data-testid="nfp-surprise-score"
    >
      <Gauge size={12} strokeWidth={1.75} />
      Surprise {score > 0 ? "+" : ""}{score}σ
      <span className="normal-case tracking-normal text-zinc-400">· {label}</span>
    </span>
  );
}

function PulseBadge({ pulse }) {
  const map = {
    hot: { Icon: Flame, cls: "text-neg border-neg/40 bg-neg/10", label: "Leaning HOT" },
    cool: { Icon: Snowflake, cls: "text-pos border-pos/40 bg-pos/10", label: "Leaning COOL" },
    mixed: { Icon: Scale, cls: "text-zinc-400 border-zinc-700 bg-zinc-800/30", label: "Mixed signals" },
  };
  const { Icon, cls, label } = map[pulse.lean] || map.mixed;
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] uppercase tracking-widest ${cls}`}
      data-testid="nfp-pulse-badge"
    >
      <Icon size={12} strokeWidth={1.75} />
      {label}
      <span className="text-zinc-500 normal-case tracking-normal">
        {pulse.positive} strong · {pulse.negative} soft{pulse.pending ? ` · ${pulse.pending} awaited` : ""}
      </span>
    </span>
  );
}

function AwaitingTable({ rows }) {
  return (
    <div className="border border-dashed border-zinc-700 bg-transparent" data-testid="nfp-table-awaiting">
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-amber-term flex items-center gap-1.5">
        <Clock size={12} strokeWidth={1.75} /> Awaiting this cycle ({rows.length})
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1.5 px-3 py-2 text-[11px]">
        {rows.map((r) => (
          <span key={`${r.key}-${r.date}`} className="text-zinc-400" data-testid={`nfp-await-${r.key}`}>
            {r.indicator} <span className="text-zinc-600">{r.date}</span>{" "}
            <span className="tabular-nums text-zinc-500">exp {fmtVal(r.consensus, r.unit)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function NFPDetailPage() {
  const [index, setIndex] = useState(null);
  const q = useQuery({
    queryKey: ["nfp-detail", index ?? "default"],
    queryFn: () => fetchNfpDetail(index),
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 90,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const nfp = d?.nfp;

  return (
    <div className="min-h-screen bg-black text-zinc-200 font-mono">
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between sticky top-0 bg-black z-10">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="nfp-back-btn" className="text-zinc-500 hover:text-amber-term transition-colors">
            <ArrowLeft size={18} strokeWidth={1.5} />
          </Link>
          <span className="text-[13px] uppercase tracking-widest text-zinc-300 flex items-center gap-2">
            <Briefcase size={14} strokeWidth={1.5} className="text-amber-term" /> NFP · Employment Situation Detail
          </span>
        </div>
        {d?.window && (
          <span className="text-[10px] uppercase tracking-widest text-zinc-600" data-testid="nfp-window">
            {d.window.from || "—"} → {d.window.to}
          </span>
        )}
      </div>

      {q.isLoading ? (
        <div className="h-[70vh] flex items-center justify-center text-zinc-600 text-[12px] uppercase tracking-widest">Loading NFP detail…</div>
      ) : q.isError ? (
        <div className="h-[70vh] flex items-center justify-center text-neg text-[12px] uppercase tracking-widest">Failed to load NFP detail</div>
      ) : (
        <div className={`max-w-6xl mx-auto px-4 py-6 space-y-6 ${q.isFetching ? "opacity-70 transition-opacity" : ""}`}>
          {/* Month board */}
          <MonthBoard months={d?.months} selected={d?.selected_index ?? index} onSelect={setIndex} />

          {/* NFP print */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                Non-Farm Payrolls · {nfp?.month}{d?.pending ? " · Upcoming (awaiting release)" : ""}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <RevisionBadge revision={nfp?.nfp_revision} revised={nfp?.nfp_revised} />
                <SurpriseScore score={nfp?.surprise_score} label={nfp?.surprise_label} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <PrintMetric testid="nfp-print-payrolls" label="Payrolls" actual={fmtJobs(nfp?.nfp)} consensus={fmtJobs(nfp?.nfp_forecast)} previous={fmtJobs(nfp?.nfp_prev)} actualRaw={nfp?.nfp} consensusRaw={nfp?.nfp_forecast} />
              <PrintMetric testid="nfp-print-unemployment" label="Unemployment" actual={fmtPct(nfp?.unemployment)} consensus={fmtPct(nfp?.unemployment_forecast)} previous={fmtPct(nfp?.unemployment_prev)} actualRaw={nfp?.unemployment} consensusRaw={nfp?.unemployment_forecast} invert />
              <PrintMetric testid="nfp-print-wages" label="Wages m/m" actual={fmtPct(nfp?.ahe)} consensus={fmtPct(nfp?.ahe_forecast)} previous={fmtPct(nfp?.ahe_prev)} actualRaw={nfp?.ahe} consensusRaw={nfp?.ahe_forecast} />
            </div>
          </div>

          {/* AI read — why the print landed as it did & why DXY reacted */}
          <NfpMonthRead index={d?.selected_index ?? index} month={nfp?.month} pending={d?.pending} />

          {/* DXY reaction */}
          <DXYPanel dxy={d?.dxy} />

          {/* Run-up indicators between the two prints, split by USD impact + hot/cool lean */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                {d?.pending
                  ? "Run-up data since the last NFP — is the next print shaping up hot or cool?"
                  : "Run-up data between this print and the previous — split by dollar impact"}
              </div>
              {d?.pulse && <PulseBadge pulse={d.pulse} />}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <StatTable title="Stronger · USD Positive" rows={d?.usd_positive || []} positive />
              <StatTable title="Weaker · USD Negative" rows={d?.usd_negative || []} positive={false} />
            </div>
            {d?.pending_indicators?.length > 0 && (
              <div className="mt-4">
                <AwaitingTable rows={d.pending_indicators} />
              </div>
            )}
            {d?.neutral?.length > 0 && (
              <div className="mt-4">
                <StatTableNeutral rows={d.neutral} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatTableNeutral({ rows }) {
  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="nfp-table-neutral">
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        <Minus size={12} strokeWidth={1.75} /> Neutral / In-line ({rows.length})
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1.5 px-3 py-2 text-[11px]">
        {rows.map((r) => (
          <span key={`${r.key}-${r.date}`} className="text-zinc-400" data-testid={`nfp-row-${r.key}-${r.date}`}>
            {r.indicator} <span className="text-zinc-600">{r.date}</span> <span className="tabular-nums text-zinc-300">{fmtVal(r.actual, r.unit)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
